import { PRESENT_VALUE_SENTINEL } from '../../../types/QueryBuilder';

// =============================================================================
// ANTLR (Observability Search) → Lezer (QueryBuilder) query converter
// =============================================================================
//
// The reverse of the metrics-side `parsedQueryToAntlr` serializer. Converts an
// ANTLR query string back into the two-colon filter format (field:op:value)
// consumed by the QueryBuilder.
//
// Supported conversions:
//   field=value         →  field:=:value
//   field!=value        →  field:!=:value
//   field>value         →  field:>:value  (>=, <, <= analogous)
//   NOT field=value     →  !field:=:value
//   AND / OR            →  AND / OR
//   (…)                 →  (…) (inner expression converted recursively)
//   any_span(…) / no_span(…) etc.
//                       →  anySpan(…) / noSpan(…) (typed expression)
//   field="*v*"         →  field:**:v
//   field="v*"          →  field:^=:v
//   field="*v"          →  field:=^:v
//   field="/regex/"     →  field://:/regex/
//   field!="/regex/"    →  field:!//:/regex/
//   field EXISTS        →  field:=:__present__
//   field NOT EXISTS    →  field:!=:__present__
//
// Limitation: multi-value ANTLR expansions such as `(field=v1 OR field=v2)`
// are NOT collapsed back to a single multi-value Lezer filter — they remain as
// parenthetical expressions in the converted output.
//
// =============================================================================

/** Quote a Lezer filter value when it contains characters outside the bare-value class. */
function quoteLezerValue(v: string): string {
  // Lezer bare values may not contain whitespace, comma, colon, quote, or parens.
  return /[\s,:"()]/.test(v) ? `"${v}"` : v;
}

/**
 * Try to parse one ANTLR-style filter (`field[op]value`) starting at `start`.
 * Returns the equivalent Lezer token string and the end offset, or null if the
 * input at that position is not a filter.
 */
function parseOneAntlrFilter(antlr: string, start: number): { lezer: string; end: number } | null {
  const ANTLR_FILTER_RE = /^([a-zA-Z0-9_.-]+)(!=|>=|<=|>|<|=)/;
  const m = ANTLR_FILTER_RE.exec(antlr.slice(start));
  if (!m) {
    return null;
  }

  const field = m[1];
  const antlrOp = m[2];
  let pos = start + m[0].length;

  // Parse the value: either a double-quoted string or bare non-whitespace chars.
  let inner: string;
  let wasQuoted = false;

  if (antlr[pos] === '"') {
    wasQuoted = true;
    let j = pos + 1;
    let content = '';
    while (j < antlr.length) {
      if (antlr[j] === '\\' && j + 1 < antlr.length) {
        // Unescape sequences produced by quoteAntlrValue (\" and \\).
        const next = antlr[j + 1];
        content += next === '"' ? '"' : next === '\\' ? '\\' : next;
        j += 2;
      } else if (antlr[j] === '"') {
        j++;
        break;
      } else {
        content += antlr[j++];
      }
    }
    inner = content;
    pos = j;
  } else {
    // Bare value ends at the next whitespace or parenthesis.
    const end = antlr.slice(pos).search(/[\s()]/);
    inner = end === -1 ? antlr.slice(pos) : antlr.slice(pos, pos + end);
    pos += inner.length;
    if (!inner) {
      return null;
    }
  }

  // Detect glob and regex patterns emitted by parsedQueryToAntlr (always in quoted strings).
  let lezerOp = antlrOp;
  let lezerVal = inner;

  // Regex form `field="/regex/"` (or `!=`) takes precedence — `/foo*/` would
  // otherwise be misclassified as a glob ends-with (=^) pattern.
  if (
    (antlrOp === '=' || antlrOp === '!=') &&
    wasQuoted &&
    inner.length > 1 &&
    inner.startsWith('/') &&
    inner.endsWith('/')
  ) {
    lezerOp = antlrOp === '=' ? '//' : '!//';
    // Keep the `/` delimiters — the Lezer side stores them as part of the value.
    lezerVal = inner;
  } else if (antlrOp === '=' && wasQuoted) {
    if (inner.startsWith('*') && inner.endsWith('*') && inner.length > 2) {
      lezerOp = '**';
      lezerVal = inner.slice(1, -1);
    } else if (inner.endsWith('*') && !inner.startsWith('*')) {
      lezerOp = '^=';
      lezerVal = inner.slice(0, -1);
    } else if (inner.startsWith('*') && !inner.endsWith('*')) {
      lezerOp = '=^';
      lezerVal = inner.slice(1);
    }
  } else if (antlrOp === '!=' && wasQuoted && inner.startsWith('*') && inner.endsWith('*') && inner.length > 2) {
    // Negated glob `field!="*v*"` is the "does not contain" operator.
    lezerOp = '!**';
    lezerVal = inner.slice(1, -1);
  }

  return { lezer: `${field}:${lezerOp}:${quoteLezerValue(lezerVal)}`, end: pos };
}

/**
 * Try to parse an `EXISTS` / `NOT EXISTS` filter (`field EXISTS` or `field NOT EXISTS`)
 * starting at `start`. Returns the equivalent Lezer token using the PRESENT_VALUE_SENTINEL
 * value (the inverse of how filterTokenToAntlr emits it) and the end offset, or null
 * if the input at that position is not an exists filter.
 *
 * `filterTokenToAntlr` emits NOT EXISTS for either `!field:=:present` *or*
 * `field:!=:present` (XOR of negation prefix and `!=` operator). Pick the
 * `!=` form on the way back so the LozengeFilter "present" toggle round-trips
 * cleanly — the operator-cell flip is the one users actually drive.
 */
function parseOneAntlrExists(antlr: string, start: number): { lezer: string; end: number } | null {
  const ANTLR_EXISTS_RE = /^([a-zA-Z0-9_.-]+)\s+(NOT\s+)?EXISTS\b/;
  const m = ANTLR_EXISTS_RE.exec(antlr.slice(start));
  if (!m) {
    return null;
  }
  const field = m[1];
  const negated = !!m[2];
  return {
    lezer: `${field}:${negated ? '!=' : '='}:${PRESENT_VALUE_SENTINEL}`,
    end: start + m[0].length,
  };
}

// ANTLR relational prefix (snake_case) → Lezer EXPRESSION.expressionType (camelCase).
// Inverse of OBS_RELATIONAL_PREFIX in the metrics-side TraceDataPreviewTable.
const ANTLR_TO_LEZER_RELATIONAL_PREFIX: Record<string, string> = {
  any_span: 'anySpan',
  any_span2: 'anySpan2',
  any_span3: 'anySpan3',
  no_span: 'noSpan',
  child_span: 'childSpan',
  parent_span: 'parentSpan',
  root_span: 'rootSpan',
  any_session: 'anySession',
};

const ANTLR_RELATIONAL_PREFIX_RE = /^([a-zA-Z_][a-zA-Z0-9_]*)\(/;

/**
 * Quickly verify that the open / close paren counts match, ignoring any parens
 * that appear inside double-quoted strings (those are value characters, not
 * structural). Returns false on mismatch — callers bail out before parsing so
 * unbalanced input doesn't produce a half-converted Lezer query.
 */
function antlrParensBalanced(antlr: string): boolean {
  let depth = 0;
  let inQuote = false;
  for (let i = 0; i < antlr.length; i++) {
    const c = antlr[i];
    if (inQuote) {
      // Skip escaped characters inside quotes (e.g. `\"` or `\\`).
      if (c === '\\' && i + 1 < antlr.length) {
        i++;
      } else if (c === '"') {
        inQuote = false;
      }
      continue;
    }
    if (c === '"') {
      inQuote = true;
    } else if (c === '(') {
      depth++;
    } else if (c === ')') {
      depth--;
      if (depth < 0) {
        return false;
      }
    }
  }
  return depth === 0;
}

/**
 * Given `start` pointing just past an opening paren (depth already 1), return
 * the index just past its matching close paren. Parens inside double-quoted
 * strings are ignored (same quote/escape handling as antlrParensBalanced) so a
 * value like `field="a)b"` doesn't close the group early.
 */
function findGroupEnd(antlr: string, start: number): number {
  let depth = 1;
  let inQuote = false;
  let j = start;
  while (j < antlr.length && depth > 0) {
    const c = antlr[j];
    if (inQuote) {
      if (c === '\\' && j + 1 < antlr.length) {
        j++;
      } else if (c === '"') {
        inQuote = false;
      }
    } else if (c === '"') {
      inQuote = true;
    } else if (c === '(') {
      depth++;
    } else if (c === ')') {
      depth--;
    }
    j++;
  }
  return j;
}

/**
 * Convert an ANTLR query string (Observability Search format) to the Lezer
 * query string expected by the QueryBuilder. This is the reverse of the
 * metrics-side `parsedQueryToAntlr` serializer.
 */
export function antlrToLezer(antlr: string): string {
  // Bail out if parens are unbalanced — the recursive descent below assumes
  // every open paren has a matching close and would otherwise emit a partial,
  // unparseable Lezer string from malformed input.
  if (!antlrParensBalanced(antlr)) {
    return '';
  }

  const parts: string[] = [];
  let i = 0;

  while (i < antlr.length) {
    // Skip whitespace between tokens.
    if (/\s/.test(antlr[i])) {
      i++;
      continue;
    }

    // Typed relational expression — `<prefix>(...)` with a known mapping.
    // Must be checked before the bare `(` handler so `any_span(...)` doesn't
    // fall through and lose its expressionType.
    const relMatch = ANTLR_RELATIONAL_PREFIX_RE.exec(antlr.slice(i));
    const lezerPrefix = relMatch ? ANTLR_TO_LEZER_RELATIONAL_PREFIX[relMatch[1]] : undefined;
    if (relMatch && lezerPrefix) {
      const startInner = i + relMatch[0].length;
      const j = findGroupEnd(antlr, startInner);
      parts.push(`${lezerPrefix}(${antlrToLezer(antlr.slice(startInner, j - 1))})`);
      i = j;
      continue;
    }

    // Parenthetical expression — recursively convert the inner expression.
    if (antlr[i] === '(') {
      const j = findGroupEnd(antlr, i + 1);
      parts.push(`(${antlrToLezer(antlr.slice(i + 1, j - 1))})`);
      i = j;
      continue;
    }

    // NOT <filter> — maps to a negated Lezer filter token.
    if (antlr.startsWith('NOT ', i)) {
      let j = i + 4;
      while (j < antlr.length && /\s/.test(antlr[j])) {
        j++;
      }
      const result = parseOneAntlrFilter(antlr, j);
      if (result) {
        parts.push(`!${result.lezer}`);
        i = result.end;
        continue;
      }
    }

    // AND / OR boolean keywords.
    if (antlr.startsWith('AND', i) && (i + 3 >= antlr.length || /\s/.test(antlr[i + 3]))) {
      parts.push('AND');
      i += 3;
      continue;
    }
    if (antlr.startsWith('OR', i) && (i + 2 >= antlr.length || /\s/.test(antlr[i + 2]))) {
      parts.push('OR');
      i += 2;
      continue;
    }

    // EXISTS / NOT EXISTS — inverse of filterTokenToAntlr's PRESENT_VALUE_SENTINEL handling.
    const existsResult = parseOneAntlrExists(antlr, i);
    if (existsResult) {
      parts.push(existsResult.lezer);
      i = existsResult.end;
      continue;
    }

    // Filter token.
    const result = parseOneAntlrFilter(antlr, i);
    if (result) {
      parts.push(result.lezer);
      i = result.end;
      continue;
    }

    // Free text fallback — consume until whitespace or parenthesis.
    let j = i;
    while (j < antlr.length && !/[\s()]/.test(antlr[j])) {
      j++;
    }
    if (j > i) {
      parts.push(antlr.slice(i, j));
    }
    i = j;
  }

  return parts.join(' ');
}
